import React, { useState, useEffect } from 'react';
import { Button, Col, Row, Input, Message, toaster } from 'rsuite';
import { FaEnvelope, FaKey } from 'react-icons/fa';
import {
  sendVerificationCode,
  verifyEmailCode,
  validateEmailFormat,
  validateVerificationCode
} from '../service/EmailVerificationService';

export interface EmailVerificationProps {
  onVerificationSuccess: (email: string) => void;
  onVerificationError?: (error: string) => void;
  defaultEmail?: string;
  userId?: number;
  title?: string;
  subtitle?: string;
}

interface EmailVerificationState {
  email: string;
  code: string;
  step: 'email' | 'code';
  isLoading: boolean;
  countdown: number;
  canResend: boolean;
  emailError: string;
  codeError: string;
}

const EmailVerification: React.FC<EmailVerificationProps> = ({
  onVerificationSuccess,
  onVerificationError,
  defaultEmail = '',
  userId,
  title = 'Email Verification',
  subtitle = 'Please verify your email address to continue'
}) => {
  const [state, setState] = useState<EmailVerificationState>({
    email: defaultEmail,
    code: '',
    step: 'email',
    isLoading: false,
    countdown: 0,
    canResend: true,
    emailError: '',
    codeError: ''
  });

  // Countdown timer for resend functionality
  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (state.countdown > 0) {
      timer = setTimeout(() => {
        setState(prev => ({
          ...prev,
          countdown: prev.countdown - 1,
          canResend: prev.countdown <= 1
        }));
      }, 1000);
    }
    return () => clearTimeout(timer);
  }, [state.countdown]);

  const handleSendCode = async () => {
    // Reset errors
    setState(prev => ({ ...prev, emailError: '', codeError: '' }));

    // Validate email
    if (!state.email.trim()) {
      setState(prev => ({ ...prev, emailError: 'Email is required' }));
      return;
    }

    if (!validateEmailFormat(state.email)) {
      setState(prev => ({ ...prev, emailError: 'Please enter a valid email address' }));
      return;
    }

    setState(prev => ({ ...prev, isLoading: true }));

    try {
      const result = await sendVerificationCode(state.email, userId);

      if (result.success) {
        toaster.push(
          <Message type="success">
            驗證碼已發送，請檢查信箱!
          </Message>,
          { placement: 'topCenter', duration: 3000 }
        );

        setState(prev => ({
          ...prev,
          step: 'code',
          countdown: 60,
          canResend: false,
          isLoading: false
        }));
      } else {
        setState(prev => ({
          ...prev,
          emailError: result.error || 'Failed to send verification code',
          isLoading: false
        }));

        if (onVerificationError) {
          onVerificationError(result.error || 'Failed to send verification code');
        }
      }
    } catch (error) {
      const errorMessage = 'Network error. Please check your connection.';
      setState(prev => ({
        ...prev,
        emailError: errorMessage,
        isLoading: false
      }));

      if (onVerificationError) {
        onVerificationError(errorMessage);
      }
    }
  };

  const handleVerifyCode = async () => {
    // Reset errors
    setState(prev => ({ ...prev, codeError: '' }));

    // Validate code
    if (!state.code.trim()) {
      setState(prev => ({ ...prev, codeError: 'Verification code is required' }));
      return;
    }

    if (!validateVerificationCode(state.code)) {
      setState(prev => ({ ...prev, codeError: 'Please enter a valid 6-digit code' }));
      return;
    }

    setState(prev => ({ ...prev, isLoading: true }));

    try {
      const result = await verifyEmailCode(state.email, state.code);

      if (result.success && result.data.isValid) {
        toaster.push(
          <Message type="success">
            驗證成功!
          </Message>,
          { placement: 'topCenter', duration: 3000 }
        );

        onVerificationSuccess(state.email);
      } else {
        setState(prev => ({
          ...prev,
          codeError: result.error || 'Invalid or expired verification code',
          isLoading: false
        }));

        if (onVerificationError) {
          onVerificationError(result.error || 'Invalid or expired verification code');
        }
      }
    } catch (error) {
      const errorMessage = 'Verification failed. Please try again.';
      setState(prev => ({
        ...prev,
        codeError: errorMessage,
        isLoading: false
      }));

      if (onVerificationError) {
        onVerificationError(errorMessage);
      }
    }
  };

  const handleResendCode = () => {
    setState(prev => ({ ...prev, code: '', codeError: '' }));
    handleSendCode();
  };

  const handleBackToEmail = () => {
    setState(prev => ({
      ...prev,
      step: 'email',
      code: '',
      codeError: '',
      countdown: 0,
      canResend: true
    }));
  };

  return (
    <div style={{ maxWidth: '400px', margin: '0 auto', padding: '20px' }}>
      <div style={{ textAlign: 'center', marginBottom: '30px' }}>
        <h4>{title}</h4>
        <p style={{ color: '#666', fontSize: '14px' }}>{subtitle}</p>
      </div>

      {state.step === 'email' ? (
        <div>
          <Row style={{ marginBottom: '20px' }}>
            <Col xs={2} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <FaEnvelope style={{ fontSize: '18px', color: '#666' }} />
            </Col>
            <Col xs={22}>
              <Input
                placeholder="請輸入您的電子郵件地址"
                value={state.email}
                onChange={(value) => setState(prev => ({ ...prev, email: value, emailError: '' }))}
                disabled={state.isLoading}
                style={{ width: '100%' }}
              />
            </Col>
          </Row>

          {state.emailError && (
            <div style={{ color: '#f44336', fontSize: '12px', marginBottom: '10px', textAlign: 'left' }}>
              {state.emailError}
            </div>
          )}

          <Button
            appearance="primary"
            block
            loading={state.isLoading}
            onClick={handleSendCode}
            disabled={state.isLoading}
          >
            {state.isLoading ? 'Sending...' : '發送驗證碼'}
          </Button>
        </div>
      ) : (
        <div>
          <Row style={{ marginBottom: '20px' }}>
            <Col xs={2} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <FaKey style={{ fontSize: '18px', color: '#666' }} />
            </Col>
            <Col xs={22}>
              <Input
                placeholder="Enter 6-digit code"
                value={state.code}
                onChange={(value) => setState(prev => ({ ...prev, code: value, codeError: '' }))}
                disabled={state.isLoading}
                maxLength={6}
                style={{ width: '100%', letterSpacing: '2px', textAlign: 'center' }}
              />
            </Col>
          </Row>

          <div style={{ textAlign: 'center', marginBottom: '15px' }}>
            <p style={{ fontSize: '12px', color: '#666' }}>
              驗證碼已發送至: <strong>{state.email}</strong>
            </p>
          </div>

          {state.codeError && (
            <div style={{ color: '#f44336', fontSize: '12px', marginBottom: '10px', textAlign: 'left' }}>
              {state.codeError}
            </div>
          )}

          <Button
            appearance="primary"
            block
            loading={state.isLoading}
            onClick={handleVerifyCode}
            disabled={state.isLoading}
            style={{ marginBottom: '10px' }}
          >
            {state.isLoading ? 'Verifying...' : 'Verify Code'}
          </Button>

          <div style={{ textAlign: 'center', marginBottom: '10px' }}>
            {state.canResend ? (
              <Button appearance="link" onClick={handleResendCode} size="xs">
                重新發送驗證碼
              </Button>
            ) : (
              <span style={{ fontSize: '12px', color: '#666' }}>
                {state.countdown}秒後可重新發送驗證碼
              </span>
            )}
          </div>

          <div style={{ textAlign: 'center' }}>
            <Button appearance="link" onClick={handleBackToEmail} size="xs">
              更改電子郵件地址
            </Button>
          </div>
        </div>
      )}

      <div style={{ marginTop: '20px', padding: '10px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
        <p style={{ fontSize: '11px', color: '#666', textAlign: 'center', margin: 0 }}>
          • 驗證碼在 10 分鐘內過期<br />
          • 如果您沒有收到電子郵件，請檢查您的垃圾郵件文件夾<br />
          • 每個電子郵件每 15 分鐘最多可嘗試 3 次
        </p>
      </div>
    </div>
  );
};

export default EmailVerification;